import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { lastValueFrom } from 'rxjs';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { User } from 'src/app/models/user.model';
import { ProductsService } from 'src/app/services/products.service';
import { UserService } from 'src/app/services/user.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss'
})
export class ProductsComponent {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator = Object.create(null);
  dataSource = new MatTableDataSource([]);
  pageSize = 5;
  user: User
  createProductModalVisible: boolean = false
  updateProductModalVisible: boolean = false
  loading: boolean = false
  public createProductForm: FormGroup;
  public updateProductForm: FormGroup;
  selectedFile: File | null = null;
  selectedFileName: string | null = null;
  imageUrl: string | null = null;
  selectedProduct: any = null;

  currencies = [
    { label: 'US Dollar (USD)', value: 'usd' },
    { label: 'Euro (EUR)', value: 'eur' },
    { label: 'British Pound (GBP)', value: 'gbp' }
  ];

  displayedColumns: string[] = [
    'product',
    'description',
    'price',
    'createdAt',
    'updatedAt',
    'actions'
  ];

  constructor(
    private productsService: ProductsService,
    private userService: UserService,
    private fb: FormBuilder
  ) {

    this.user = this.userService.user

    this.createProductForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      currency: ['', Validators.required],
      price: ['', Validators.required],
      image: ['']
    })

    this.updateProductForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      currency: ['', Validators.required],
      price: ['', Validators.required],
      image: ['']
    })

    this.loadProducts()
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  async loadProducts() {
    this.loading = true
    const productsObject = await lastValueFrom(this.productsService.getAllProduct())
    this.dataSource.data = Object.values(productsObject).flat()
    
    await Promise.all(
      this.dataSource.data.map(async (product) => {
        const pricesObject = await lastValueFrom(
          this.productsService.getPriceFromProduct(product.id, this.user.connectedStripeId)
        );
        product.prices = Object.values(pricesObject).flat();
        this.loading = false
      })
    );
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.selectedFileName = this.selectedFile.name;
    }
  }

  async createProduct() {
    this.loading = true
    if (this.createProductForm.valid && this.selectedFile) {
      try {
        const base64Data = await this.convertFileToBase64(this.selectedFile);

        const uploadResponse: any = await lastValueFrom(
          this.productsService.uploadImage(
            this.selectedFile.name,
            base64Data
          )
        );

        this.imageUrl = uploadResponse.fileUrl;

        const rawPrice = this.createProductForm.value.price;
        const priceInCents = rawPrice ? Math.round(rawPrice * 100) : 0;

        const payload = {
          ...this.createProductForm.value,
          price: priceInCents,
          image: this.imageUrl,
        };

        await lastValueFrom(this.productsService.addProduct(payload));

        this.createProductModalVisible = false;
        this.createProductForm.reset();
        this.selectedFile = null;
        this.imageUrl = null;
        await this.loadProducts();
        this.loading = false
      } catch (err) {
        console.error('Error creating product:', err);
        this.loading = false
      }
    }
  }

  async updateProduct() {
    this.loading = true;
  
    try {
      // Copiamos los valores del formulario
      const payload: any = { ...this.updateProductForm.value };
  
      // Solo procesamos la imagen si hay un archivo seleccionado
      if (this.selectedFile) {
        const base64Data = await this.convertFileToBase64(this.selectedFile);
  
        const uploadResponse: any = await lastValueFrom(
          this.productsService.uploadImage(
            this.selectedFile.name,
            base64Data
          )
        );
  
        this.imageUrl = uploadResponse.fileUrl;
        payload.image = this.imageUrl;
      } else {
        // No hay archivo seleccionado → eliminamos la propiedad image
        delete payload.image;
      }
  
      // Convertimos el precio a cents si viene
      const rawPrice = this.updateProductForm.value.price;
      if (rawPrice != null) {
        payload.price = Math.round(rawPrice * 100);
      }
  
      await lastValueFrom(
        this.productsService.updateProduct(this.selectedProduct.id, payload)
      );
  
      this.updateProductModalVisible = false;
      this.updateProductForm.reset();
      this.selectedFile = null;
      this.selectedProduct = null;
      this.imageUrl = null;
      await this.loadProducts();
    } catch (err) {
      Swal.fire('Error updating product', err.message, 'error');
    } finally {
      this.loading = false;
    }
  }  

  editProduct(productId: any): void {
    this.loading = true;
    this.updateProductModalVisible = true;

    // Resetear el formulario y limpiar variables antes de cargar nuevos datos
    this.updateProductForm.reset();
    this.selectedFile = null;
    this.selectedFileName = null;
    this.imageUrl = null;
    this.selectedProduct = null;

    // Encuentra el producto
    this.selectedProduct = this.dataSource.data.find(
      (product) => product.id === productId
    );

    if (!this.selectedProduct) {
      this.loading = false;
      return;
    }

    let currencyValue = this.selectedProduct?.currency?.toLowerCase();
    if (!currencyValue && this.selectedProduct.prices?.[0]?.currency) {
      currencyValue = this.selectedProduct.prices[0].currency.toLowerCase();
    }

    // Verificar que el valor existe en las opciones
    const currencyExists = this.currencies.some(c => c.value === currencyValue);

    // Precio: convertir cents a unidad
    const price = this.selectedProduct.prices?.[0]?.unit_amount ?? null;

    this.imageUrl = this.selectedProduct.images?.[0] ?? null;

    this.updateProductForm.patchValue({
      name: this.selectedProduct.name ?? '',
      description: this.selectedProduct.description ?? '',
      currency: currencyExists ? currencyValue : null,
      price: price ? price / 100 : null
    });

    this.loading = false;
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  }

  showCreateProductModal() {
    this.createProductModalVisible = true
  }
}
